/**
 * Authentication state.
 *
 * `hasPermission` here decides what the interface OFFERS. It never decides
 * what the system ALLOWS — every call this app makes is authorized again by
 * the Worker against the database. If this file were replaced wholesale by an
 * attacker in the browser, they would gain a more cluttered menu and nothing
 * else.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CurrentUser, PermissionKey } from "@laundrypro/shared";
import { ApiError, apiFetch, setCsrfToken } from "../lib/api";

interface AuthState {
  readonly user: CurrentUser | null;
  readonly loading: boolean;
  readonly permissions: ReadonlySet<PermissionKey>;
  readonly hasPermission: (...keys: readonly PermissionKey[]) => boolean;
  readonly login: (username: string, password: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly refresh: () => Promise<void>;
  /** Set when the account must replace an administrator-assigned password. */
  readonly mustChangePassword: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

interface SessionResponse {
  user: CurrentUser;
  csrfToken: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<SessionResponse>("/auth/me");
      setCsrfToken(data.csrfToken);
      setUser(data.user);
      setMustChangePassword(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === "password_change_required") {
        // The session is valid; it is simply confined to the password-change
        // screen until a new password is set.
        setMustChangePassword(true);
        return;
      }
      setUser(null);
      setCsrfToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Recovers an existing session on page load: the cookie survives a reload,
  // but the in-memory CSRF token does not, so it has to be re-fetched.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const data = await apiFetch<SessionResponse>("/auth/login", {
      method: "POST",
      body: { username, password },
    });
    setCsrfToken(data.csrfToken);
    setUser(data.user);
    setMustChangePassword(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } finally {
      // Local state is cleared even if the request failed, so a network error
      // cannot leave the UI showing a signed-in state the server has ended.
      setUser(null);
      setCsrfToken(null);
      setMustChangePassword(false);
    }
  }, []);

  const permissions = useMemo(
    () => new Set<PermissionKey>(user?.permissions ?? []),
    [user],
  );

  const hasPermission = useCallback(
    (...keys: readonly PermissionKey[]) => keys.every((key) => permissions.has(key)),
    [permissions],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, permissions, hasPermission, login, logout, refresh, mustChangePassword }),
    [user, loading, permissions, hasPermission, login, logout, refresh, mustChangePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
}
